<script setup>
import { $api } from '@/services';
import { useRoute } from 'vue-router';
import { computed, ref, onMounted } from 'vue';
import dayjs from 'dayjs';
import DaInfinityLoading from '@/components/common/daInfinityLoading/DaInfinityLoading.vue';
import StudentMediaViewer from './StudentMediaViewer.vue';
import fallbackImage from '@/assets/images/background/auth.png';

const route = useRoute();

const studentId = computed(() => {
	return route.params.id;
});

const mediaMonths = ref([]);
const monthStart = ref(null);
const stopLoad = ref(false);
const isFetching = ref(false);
const imageShow = ref(false);
const photos = ref([]);
const photosIndex = ref(0);

const hasMedia = computed(() =>
	mediaMonths.value.some((month) => month.items.length)
);

const getStudentMedia = async () => {
	if (stopLoad.value || isFetching.value) return;

	isFetching.value = true;
	try {
		const response = await $api.student.getStudentMedia(
			studentId.value,
			monthStart.value ? { month_start: monthStart.value } : {}
		);
		const data = response?.data?.data || [];
		const currentMonthStart = response?.data?.month_start;
		const previousMonth =
			response?.data?.previous_month ||
			response?.data?.previous_month_start;

		if (!currentMonthStart) {
			stopLoad.value = true;
			return;
		}

		const alreadyLoaded = mediaMonths.value.some(
			(month) => month.monthStart === currentMonthStart
		);
		if (!alreadyLoaded && data.length) {
			mediaMonths.value.push({
				monthStart: currentMonthStart,
				items: data,
				collapsed: false,
			});
		}

		if (previousMonth) {
			monthStart.value = previousMonth;
		} else {
			stopLoad.value = true;
		}
	} catch (error) {
		console.error(error);
		stopLoad.value = true;
	} finally {
		isFetching.value = false;
	}
};

const formatMonthTitle = (value) => {
	return `Tháng ${dayjs(value).format('MM/YYYY')}`;
};

const formatMediaDate = (value) => {
	return value ? dayjs(value).format('DD/MM/YYYY') : '';
};

const mediaThumbnail = (media) => {
	if (media?.asset_type === 'video') {
		return media.video_poster_url || fallbackImage;
	}
	return media?.asset_url || fallbackImage;
};

const toggleMonth = (month) => {
	month.collapsed = !month.collapsed;
};

const openMedia = (month, item) => {
	const list = month.items || [];
	photos.value = list;
	const index = list.findIndex((media) => media.id === item.id);
	photosIndex.value = index >= 0 ? index : 0;
	imageShow.value = true;
};

onMounted(() => {
	getStudentMedia();
});
</script>
<template>
	<StudentMediaViewer
		v-if="imageShow && photos.length"
		:images="photos"
		:index="photosIndex"
		@close="imageShow = false"
	/>
	<div class="student-media pt-6">
		<div
			v-if="stopLoad && !hasMedia && !isFetching"
			class="text-center text-neutral-500 py-10"
		>
			Học sinh này hiện chưa có hình ảnh / video nào được tải lên.
		</div>
		<div
			v-for="month in mediaMonths"
			:key="month.monthStart"
			class="mb-8"
		>
			<div class="flex items-center justify-between mb-4">
				<div class="text-neutral-500 font-semibold">
					{{ formatMonthTitle(month.monthStart) }}
				</div>
				<button
					type="button"
					class="
						flex
						items-center
						text-neutral-400 text-sm
						font-semibold
						cursor-pointer
					"
					@click="toggleMonth(month)"
				>
					<svg
						class="mr-1 transform"
						:class="{ 'rotate-180': month.collapsed }"
						width="16"
						height="16"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M15 12.5L10 7.5L5 12.5"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
					<span>{{ month.collapsed ? 'Mở rộng' : 'Thu gọn' }}</span>
				</button>
			</div>
			<div
				v-if="!month.collapsed"
				class="
					grid grid-cols-2
					md:grid-cols-3
					lg:grid-cols-5
					gap-4
				"
			>
				<div
					v-for="item in month.items"
					:key="item.id"
					class="
						relative
						rounded-xl
						overflow-hidden
						cursor-pointer
						bg-neutral-200
					"
					style="aspect-ratio: 4 / 3"
					@click="openMedia(month, item)"
				>
					<img
						:src="mediaThumbnail(item)"
						alt=""
						class="w-full h-full object-cover"
					/>
					<div
						class="
							absolute
							inset-0
							bg-gradient-to-t
							from-black/55
							via-black/10
							to-transparent
						"
					></div>
					<div
						v-if="item.asset_type === 'video'"
						class="
							absolute
							flex
							items-center
							justify-center
							inset-0
						"
					>
						<svg
							fill="none"
							height="44"
							viewBox="0 0 55 55"
							width="44"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g opacity="0.5">
								<rect
									fill="black"
									height="54.5"
									rx="27.25"
									width="54.5"
								/>
								<path
									d="M34.4948 28.5672L24.9024 36.9606C23.7709 37.9507 22 37.1471 22 35.6436V18.8568C22 17.3532 23.7709 16.5497 24.9024 17.5397L34.4948 25.9332C35.2917 26.6304 35.2917 27.8699 34.4948 28.5672Z"
									fill="white"
								/>
							</g>
						</svg>
					</div>
					<div
						v-if="item.date"
						class="absolute bottom-7 left-4 z-10 text-white text-sm font-medium"
					>
						{{ item.skill_name }}
					</div>
					<div
						v-if="item.date"
						class="absolute bottom-2 left-4 z-10 text-white text-sm font-medium"
					>
						{{ formatMediaDate(item.date) }}
					</div>
				</div>
			</div>
		</div>
		<DaInfinityLoading
			:loaded="stopLoad"
			:is-loading="isFetching"
			@infinite-load="getStudentMedia"
		/>
	</div>
</template>
